import type React from 'react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { LoadingPage } from '@/components/ui/loading';
import { adminService } from '@/services/admin.service';

const RETURN_AFTER_LOGIN_KEY = 'LIZTA_RETURN_AFTER_LOGIN';

function getReturnPath(pathname: string, search: string) {
  return `${pathname}${search}`;
}

function persistReturnPath(pathname: string, search: string) {
  const returnPath = getReturnPath(pathname, search);
  if (returnPath === '/login' || returnPath.startsWith('/auth/callback')) {
    return;
  }

  try {
    sessionStorage.setItem(RETURN_AFTER_LOGIN_KEY, returnPath);
  } catch {
    // sessionStorage pode estar indisponível em alguns contextos privados.
  }
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, initialized } = useAuthStore();
  const location = useLocation();

  // Mostra loading enquanto está inicializando
  if (!initialized || (loading && !user)) {
    return <LoadingPage message="Verificando autenticação..." />;
  }

  // Se inicializou e não tem usuário, redireciona para login
  if (initialized && !user) {
    persistReturnPath(location.pathname, location.search);
    return <Navigate to="/login" replace state={{ from: getReturnPath(location.pathname, location.search) }} />;
  }

  // Renderiza children apenas se tem usuário
  return <>{children}</>;
}

export function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, initialized } = useAuthStore();
  const location = useLocation();
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!initialized || loading) {
      return;
    }

    if (!user) {
      setIsCheckingAdmin(false);
      setIsAdmin(false);
      return;
    }

    setIsCheckingAdmin(true);
    adminService.checkIsAdmin().then((allowed) => {
      if (cancelled) return;
      setIsAdmin(allowed);
      setIsCheckingAdmin(false);
    });

    return () => {
      cancelled = true;
    };
  }, [initialized, loading, user]);

  if (!initialized || (loading && !user) || isCheckingAdmin) {
    return <LoadingPage message="Verificando permissão..." />;
  }

  if (!user) {
    persistReturnPath(location.pathname, location.search);
    return <Navigate to="/login" replace state={{ from: getReturnPath(location.pathname, location.search) }} />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

